import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Workflow, Loader2, Edit3, Copy, ExternalLink, CopyPlus, Archive } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
    draft: 'bg-stone-100 text-stone-500',
    published: 'bg-emerald-100 text-emerald-700',
    archived: 'bg-amber-100 text-amber-700',
};

export default function FormFlows() {
    const { getAuthHeader, user } = useAuth();
    const navigate = useNavigate();
    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState('');
    const [creating, setCreating] = useState(false);
    const isAdmin = user?.is_admin || user?.is_super_admin;

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/form-flows`, { headers: getAuthHeader() });
            setFlows(res.data || []);
        } catch { toast.error('Failed to load form flows'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async () => {
        if (!createName.trim()) { toast.error('Name required'); return; }
        setCreating(true);
        try {
            const res = await axios.post(`${API}/form-flows`, { name: createName }, { headers: getAuthHeader() });
            toast.success('Form flow created');
            setShowCreate(false); setCreateName('');
            navigate(`/form-flows/${res.data.flow_id}/edit`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
        finally { setCreating(false); }
    };

    const duplicate = async (flow) => {
        try { await axios.post(`${API}/form-flows/${flow.flow_id}/duplicate`, {}, { headers: getAuthHeader() }); toast.success('Duplicated'); load(); }
        catch { toast.error('Failed to duplicate'); }
    };

    const archive = async (flow) => {
        if (!window.confirm(`Archive "${flow.name}"?`)) return;
        try { await axios.post(`${API}/form-flows/${flow.flow_id}/archive`, {}, { headers: getAuthHeader() }); toast.success('Archived'); load(); }
        catch { toast.error('Failed to archive'); }
    };

    const publicUrl = (slug) => `${window.location.origin}/flow/${slug}`;
    const copyUrl = (slug) => { navigator.clipboard.writeText(publicUrl(slug)); toast.success('Public URL copied'); };

    const filtered = flows.filter(f => f.name?.toLowerCase().includes(search.toLowerCase()));

    if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>;

    return (
        <div className="min-h-screen bg-stone-50 p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Form Flows</h1>
                    <p className="text-sm text-stone-500 mt-1">Multi-step qualification flows that capture leads with UTM attribution.</p>
                </div>
                {isAdmin && <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-1.5" /> New Flow</Button>}
            </div>

            <div className="relative max-w-sm mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search flows..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
            </div>

            {filtered.length === 0 ? (
                <div className="text-center py-16">
                    <Workflow className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-400">No form flows yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(f => {
                        const stepCount = (f.steps || []).reduce((n, s) => n + (s.questions?.length || 0), 0);
                        return (
                            <div key={f.flow_id} className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-semibold text-stone-900 truncate">{f.name}</h3>
                                        <span className="text-xs text-stone-400 font-mono">/flow/{f.slug}</span>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[f.status] || STATUS_BADGE.draft}`}>{f.status}</span>
                                </div>
                                {f.custom_domain && <p className="text-xs text-stone-500 mb-1 truncate">🌐 {f.custom_domain}</p>}
                                <div className="flex items-center gap-4 text-xs text-stone-500 mb-3">
                                    <span>{stepCount} questions</span>
                                    <span>{f.submission_count || 0} leads</span>
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <Button variant="outline" size="sm" onClick={() => navigate(`/form-flows/${f.flow_id}/edit`)}><Edit3 className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                                    <Button variant="outline" size="sm" onClick={() => copyUrl(f.slug)}><Copy className="w-3.5 h-3.5 mr-1" /> URL</Button>
                                    {f.status === 'published' && (
                                        <Button variant="outline" size="sm" onClick={() => window.open(publicUrl(f.slug), '_blank', 'noopener')}><ExternalLink className="w-3.5 h-3.5" /></Button>
                                    )}
                                    {isAdmin && <Button variant="outline" size="sm" onClick={() => duplicate(f)}><CopyPlus className="w-3.5 h-3.5" /></Button>}
                                    {isAdmin && f.status !== 'archived' && <Button variant="outline" size="sm" className="text-amber-600" onClick={() => archive(f)}><Archive className="w-3.5 h-3.5" /></Button>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Create Form Flow</DialogTitle></DialogHeader>
                    <div className="py-2">
                        <label className="block text-xs font-medium text-stone-600 mb-1">Flow Name</label>
                        <input value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. VSL Qualifier" autoFocus
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            onKeyDown={e => e.key === 'Enter' && handleCreate()} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Create'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
